import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue, update } from 'firebase/database';
import { database } from '../../firebase';

const Profile = () => {
  const { employeeContext } = useAuth();
  const { companyId, employeeId } = employeeContext || {};

  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    phone: '',
    address: {
        line1: '',
        city: '',
        postalCode: '',
        country: ''
    },
    emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
    }
  });

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const empRef = ref(database, `companies/${companyId}/employees/${employeeId}`);
    const unsubscribe = onValue(empRef, (snap) => {
      const data = snap.val();
      if (data) {
        setEmployeeData(data);
        setFormData({
            phone: data.contact?.phone || data.profile?.phone || '',
            address: {
                line1: data.contact?.address?.line1 || '',
                city: data.contact?.address?.city || '',
                postalCode: data.contact?.address?.postalCode || '',
                country: data.contact?.address?.country || ''
            },
            emergencyContact: {
                name: data.contact?.emergencyContacts?.[0]?.name || '',
                phone: data.contact?.emergencyContacts?.[0]?.phone || '',
                relationship: data.contact?.emergencyContacts?.[0]?.relationship || ''
            }
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, employeeId]);

  const handleChange = (section, field, value) => {
    if (section === 'root') {
        setFormData(prev => ({ ...prev, [field]: value }));
    } else {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
        const updates = {};
        updates[`companies/${companyId}/employees/${employeeId}/contact/phone`] = formData.phone;
        updates[`companies/${companyId}/employees/${employeeId}/profile/phone`] = formData.phone;
        updates[`companies/${companyId}/employees/${employeeId}/contact/address`] = formData.address;
        
        const emergencyContacts = [{
            name: formData.emergencyContact.name,
            phone: formData.emergencyContact.phone,
            relationship: formData.emergencyContact.relationship
        }];
        updates[`companies/${companyId}/employees/${employeeId}/contact/emergencyContacts`] = emergencyContacts;

        await update(ref(database), updates);
        alert('Profile updated successfully!');
        setIsEditing(false);
    } catch (error) {
        console.error("Error updating profile:", error);
        alert("Failed to update profile.");
    } finally {
        setSaving(false);
    }
  };

  if (loading) return <div>Loading profile...</div>;
  if (!employeeData) return <div>Employee data not found.</div>;

  return (
    <div className="profile-container">
      <div className="page-header">
        <div>
            <h1 className="page-title">My Profile</h1>
            <p className="page-subtitle">Manage your personal information</p>
        </div>
        <button 
            className={`edit-toggle-btn ${isEditing ? 'active' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
        >
            <i className={`fa-solid ${isEditing ? 'fa-times' : 'fa-pen'}`}></i>
            {isEditing ? 'Cancel Edit' : 'Edit Profile'}
        </button>
      </div>

      <div className="profile-grid">
        {/* Main Info Card */}
        <div className="section-card main-card">
            <div className="profile-avatar">
                {employeeData.profile?.name?.charAt(0) || 'U'}
            </div>
            <div className="profile-identity">
                <h2>{employeeData.profile?.name || employeeData.name}</h2>
                <span className="role-tag">{employeeData.role}</span>
                <div className="id-tag">ID: {employeeData.employeeId}</div>
            </div>
            
            <div className="info-list">
                <div className="info-item">
                    <label>Department</label>
                    <div>{employeeData.department || '—'}</div>
                </div>
                <div className="info-item">
                    <label>Manager</label>
                    <div>{employeeData.managerName || '—'}</div>
                </div>
                <div className="info-item">
                    <label>Email</label>
                    <div>{employeeData.contact?.workEmail || '—'}</div>
                </div>
                <div className="info-item">
                    <label>Start Date</label>
                    <div>{employeeData.profile?.startDate || '—'}</div>
                </div>
            </div>
        </div>

        {/* Details Form/View */}
        <div className="details-column">
            <form onSubmit={handleSave}>
                {/* Contact Section */}
                <div className="section-card">
                    <h3 className="card-title"><i className="fa-solid fa-address-card"></i> Contact Information</h3>
                    <div className="form-group">
                        <label>Phone Number</label>
                        {isEditing ? (
                            <input 
                                type="text" 
                                value={formData.phone}
                                onChange={(e) => handleChange('root', 'phone', e.target.value)}
                                placeholder="+1 (555) 000-0000"
                            />
                        ) : (
                            <div className="read-only-value">{employeeData.contact?.phone || '—'}</div>
                        )}
                    </div>
                </div>

                {/* Address Section */}
                <div className="section-card">
                    <h3 className="card-title"><i className="fa-solid fa-map-location-dot"></i> Address</h3>
                    <div className="form-group">
                        <label>Street Address</label>
                        {isEditing ? (
                            <input 
                                type="text" 
                                value={formData.address.line1}
                                onChange={(e) => handleChange('address', 'line1', e.target.value)}
                            />
                        ) : (
                            <div className="read-only-value">{employeeData.contact?.address?.line1 || '—'}</div>
                        )}
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>City</label>
                            {isEditing ? (
                                <input 
                                    type="text" 
                                    value={formData.address.city}
                                    onChange={(e) => handleChange('address', 'city', e.target.value)}
                                />
                            ) : (
                                <div className="read-only-value">{employeeData.contact?.address?.city || '—'}</div>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Postal Code</label>
                            {isEditing ? (
                                <input 
                                    type="text" 
                                    value={formData.address.postalCode}
                                    onChange={(e) => handleChange('address', 'postalCode', e.target.value)}
                                />
                            ) : (
                                <div className="read-only-value">{employeeData.contact?.address?.postalCode || '—'}</div>
                            )}
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Country</label>
                        {isEditing ? (
                            <input 
                                type="text" 
                                value={formData.address.country}
                                onChange={(e) => handleChange('address', 'country', e.target.value)}
                            />
                        ) : (
                            <div className="read-only-value">{employeeData.contact?.address?.country || '—'}</div>
                        )}
                    </div>
                </div>

                {/* Emergency Contact Section */}
                <div className="section-card">
                    <h3 className="card-title"><i className="fa-solid fa-truck-medical"></i> Emergency Contact</h3>
                    <div className="form-group">
                        <label>Contact Name</label>
                        {isEditing ? (
                            <input 
                                type="text" 
                                value={formData.emergencyContact.name}
                                onChange={(e) => handleChange('emergencyContact', 'name', e.target.value)}
                            />
                        ) : (
                            <div className="read-only-value">{employeeData.contact?.emergencyContacts?.[0]?.name || '—'}</div>
                        )}
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Relationship</label>
                            {isEditing ? (
                                <input 
                                    type="text" 
                                    value={formData.emergencyContact.relationship}
                                    onChange={(e) => handleChange('emergencyContact', 'relationship', e.target.value)}
                                />
                            ) : (
                                <div className="read-only-value">{employeeData.contact?.emergencyContacts?.[0]?.relationship || '—'}</div>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Phone</label>
                            {isEditing ? (
                                <input 
                                    type="text" 
                                    value={formData.emergencyContact.phone}
                                    onChange={(e) => handleChange('emergencyContact', 'phone', e.target.value)}
                                />
                            ) : (
                                <div className="read-only-value">{employeeData.contact?.emergencyContacts?.[0]?.phone || '—'}</div>
                            )}
                        </div>
                    </div>
                </div>

                {isEditing && (
                    <div className="form-actions">
                        <button type="submit" className="save-btn" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </form>
        </div>
      </div>

      <style>{`
        .profile-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }
        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
        }
        .page-title {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            font-family: 'Montserrat', sans-serif;
        }
        .page-subtitle {
            color: rgba(255,255,255,0.7);
            font-family: 'Montserrat', sans-serif;
        }
        .edit-toggle-btn {
            background: rgba(86, 141, 237, 0.1);
            border: 1px solid rgba(86, 141, 237, 0.2);
            color: #568ded;
            padding: 10px 20px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
        }
        .edit-toggle-btn:hover {
            background: rgba(86, 141, 237, 0.2);
        }
        .edit-toggle-btn.active {
            background: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.2);
            color: white;
        }

        .profile-grid {
            display: grid;
            grid-template-columns: 300px 1fr;
            gap: 24px;
        }
        @media (max-width: 900px) {
            .profile-grid {
                grid-template-columns: 1fr;
            }
        }

        .section-card {
            background: rgba(86, 141, 237, 0.08);
            border: 1px solid rgba(86, 141, 237, 0.15);
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 24px;
        }
        .card-title {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #fff;
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: 'Montserrat', sans-serif;
        }
        .card-title i {
            color: #568ded;
        }

        /* Main Info Card */
        .main-card {
            text-align: center;
            height: fit-content;
        }
        .profile-avatar {
            width: 80px;
            height: 80px;
            background: #568ded;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            font-weight: 700;
            margin: 0 auto 16px;
        }
        .profile-identity h2 {
            font-size: 20px;
            margin: 0 0 8px 0;
            font-family: 'Montserrat', sans-serif;
        }
        .role-tag {
            background: rgba(86, 141, 237, 0.15);
            color: #568ded;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            display: inline-block;
            margin-bottom: 8px;
        }
        .id-tag {
            font-size: 12px;
            color: rgba(255,255,255,0.5);
            margin-bottom: 24px;
        }
        .info-list {
            text-align: left;
            border-top: 1px solid rgba(255,255,255,0.1);
            padding-top: 20px;
        }
        .info-item {
            margin-bottom: 16px;
        }
        .info-item label {
            font-size: 11px;
            text-transform: uppercase;
            color: rgba(255,255,255,0.5);
            display: block;
            margin-bottom: 4px;
        }
        .info-item div {
            font-size: 14px;
            color: rgba(255,255,255,0.9);
        }

        /* Forms */
        .form-group {
            margin-bottom: 16px;
        }
        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }
        .form-group label {
            display: block;
            font-size: 13px;
            color: rgba(255,255,255,0.6);
            margin-bottom: 6px;
        }
        .read-only-value {
            padding: 10px 0;
            font-size: 15px;
            color: white;
            border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        input {
            width: 100%;
            padding: 10px 12px;
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            color: white;
            font-family: 'Montserrat', sans-serif;
            box-sizing: border-box;
        }
        input:focus {
            outline: none;
            border-color: #568ded;
            background: rgba(0,0,0,0.3);
        }
        .form-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
        }
        .save-btn {
            background: #568ded;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s;
        }
        .save-btn:hover {
            background: #4a7bc7;
        }
        .save-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default Profile;
